import type { AxiosInstance } from 'axios';
import { unwrap, unwrapItems } from '../core/envelope';
import type { Product } from '../data/product';
import type { Category } from '../data/category';
import type { Banner } from '../data/banner';
import type { Brand } from '../data/brand';
import type { StorefrontMenu } from '../data/storefrontMenu';

/**
 * Shape returned by anonymous `GET /api/Public/pages/{slug}`. Distinct from
 * the authenticated {@link PageContent} row — audit + tenant columns are
 * stripped, and every field is a plain string (empty rather than null) so the
 * storefront can drop the values straight into the DOM.
 */
export interface PublicPage {
  slug: string;
  title: string;
  body: string;
  metaTitle: string;
  metaDescription: string;
  contactPhone: string;
  contactEmail: string;
  contactAddress: string;
  contactWhatsapp: string;
}

/**
 * Delivery terms for the storefront's default store, as served by
 * anonymous `POST /api/Public/shipping`.
 *
 * Anonymous by necessity: the cart page has no session and still has to
 * quote a fee, so it cannot read Store rows through the authenticated
 * `/api/Store` surface.
 */
export interface PublicShipping {
  /** Flat delivery fee. `0` = always free. */
  shippingCharge: number;
  /** Subtotal at or above which the fee is waived. `null` = never waived. */
  freeShippingThreshold: number | null;
}

/**
 * Terms to assume when the tenant has configured no store, or the
 * lookup fails.
 *
 * Free, with no threshold. Quoting nothing is the only safe default:
 * inventing a fee the merchant never configured is exactly how the
 * previous hardcoded ₹99 came to be charged on every order.
 */
export const FREE_SHIPPING: PublicShipping = {
  shippingCharge: 0,
  freeShippingThreshold: null,
};

/**
 * The delivery fee for a given order subtotal.
 *
 * Single source of truth for the rule, exported so the cart, the
 * checkout summary and the invoice body all resolve the same number —
 * the previous ₹99 lived as a separate literal in each, and the product
 * page advertised a third, contradictory threshold.
 *
 * The threshold is inclusive (`subtotal >= threshold` ships free), which
 * is what "free shipping over ₹999" means to a shopper looking at a
 * ₹999 cart.
 */
export function shippingFor(subtotal: number, terms: PublicShipping): number {
  const fee = Number(terms.shippingCharge) || 0;
  if (fee <= 0) return 0;
  const threshold = terms.freeShippingThreshold;
  if (threshold != null && Number.isFinite(Number(threshold)) && subtotal >= Number(threshold)) {
    return 0;
  }
  return fee;
}

/**
 * Server-side product query. Mirrors `PublicProductQuery` on the API
 * (`PublicStorefrontService.GetProductsAsync`) field for field — every
 * option here is applied in SQL/LINQ before paging, so the client no
 * longer has to download the catalog to filter it.
 *
 * Omit a field to leave that dimension unfiltered. `sortBy` accepts the
 * server's vocabulary, which is NOT the storefront's `SortId` — see
 * {@link PUBLIC_SORT_BY} for the mapping.
 */
export interface PublicProductQuery {
  categoryId?: string;
  brandId?: string;
  /** Brand NAME, matched case-insensitively. Independent of `brandId`. */
  brand?: string;
  /** Free text across name, sku, brand, descriptions, tags and slug. */
  search?: string;
  minPrice?: number;
  maxPrice?: number;
  /** `true` → only products with on-hand stock. */
  inStock?: boolean;
  sortBy?: PublicSortBy;
  /** 1-based. Defaults to 1 server-side. */
  page?: number;
  /** Defaults to 24 server-side, capped at 100. */
  pageSize?: number;
}

/** Sort keys the API understands. Anything else falls back to popularity. */
export type PublicSortBy = 'popularity' | 'rating' | 'newest' | 'price-asc' | 'price-desc';

/**
 * Storefront `SortId` → API `sortBy`.
 *
 * The two vocabularies differ on two keys (`pop`/`popularity`,
 * `new`/`newest`), and sending the storefront's spelling silently lands
 * in the server's `_ =>` default rather than erroring — so route every
 * sort through this map rather than passing the raw id.
 */
export const PUBLIC_SORT_BY: Readonly<Record<string, PublicSortBy>> = {
  pop: 'popularity',
  new: 'newest',
  rating: 'rating',
  'price-asc': 'price-asc',
  'price-desc': 'price-desc',
};

/**
 * One page of products plus the envelope the API returns alongside it.
 * `total` is the count BEFORE paging, so it drives the pager and the
 * "N results" label.
 */
export interface PublicProductPage {
  items: Product[];
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
}

/**
 * Anonymous, read-only storefront catalog client.
 *
 * The live API ships a small set of `/api/Public/{products,categories,
 * banners,brands}` endpoints that resolve the active tenant from the
 * `X-Tenant-Code` request header. They DO NOT require Bearer auth, so
 * the public storefront at `eshope.kristysoftware.com` can browse the
 * real catalog without forcing a sign-in first.
 *
 * Why a separate service (vs. the existing `productService`)?
 *   - Tenant header: the authed services rely on the JWT carrying a
 *     `tenantId` claim. Anonymous traffic has no JWT, so the tenant
 *     must be explicitly provided per-request.
 *   - Different response shape: `/Public/products` wraps the array in
 *     `{ items: [...] }` while `/Public/{categories,banners,brands}`
 *     return a plain array. `unwrapItems` smooths over the difference.
 *   - Read-only: no `create` / `update` / `delete` to leak.
 */

export interface PublicCatalogService {
  /**
   * Query one page of products, filtered and sorted BY THE SERVER.
   *
   * Prefer this over {@link allProducts} for anything user-facing: it
   * pushes the work the storefront used to do in a `useMemo` over the
   * whole catalog down to the API, which already implements every
   * filter and sort (see {@link PublicProductQuery}).
   *
   * Returns the paging envelope, not a bare array, so callers can render
   * a pager and a result count without a second request.
   */
  products: (query?: PublicProductQuery) => Promise<PublicProductPage>;
  /**
   * Every active product for the tenant, across all pages.
   *
   * `/Public/products` is paginated and defaults to `pageSize: 24`, so
   * posting an empty body returned only the first 24 rows — and every
   * storefront consumer filtered and sorted client-side, which meant the
   * whole catalog silently behaved as if it were 24 products. This walks
   * every page (100 at a time, the server's cap).
   *
   * Reserve it for consumers that genuinely need the full set — deriving
   * menus, computing facet counts. Anything paginated or filtered should
   * call {@link products} instead of downloading the catalog to slice it.
   */
  allProducts: () => Promise<Product[]>;
  /**
   * Fetch a single product by id. The live endpoint accepts the GUID
   * `productId` as a path segment (NOT the slug). Returns `null` when
   * the product is missing or inactive instead of throwing, so the
   * detail page can render a clean "not found" state.
   */
  productById: (id: string) => Promise<Product | null>;
  categories: () => Promise<Category[]>;
  banners: () => Promise<Banner[]>;
  brands: () => Promise<Brand[]>;
  /**
   * Delivery terms for the tenant's default store. Never throws — a
   * tenant with no store, or an unreachable API, resolves to
   * {@link FREE_SHIPPING} rather than letting the cart quote a fee that
   * was never configured.
   */
  shipping: () => Promise<PublicShipping>;
  /**
   * Fetch a CMS page (Our Story / Contact Us / FAQ / etc.) by slug.
   * Returns `null` when the tenant has no active row for the slug so the
   * storefront can render a friendly "coming soon" placeholder instead of
   * crashing. Slug travels in the path; tenant resolves from the standard
   * host / header chain.
   */
  getPage: (slug: string) => Promise<PublicPage | null>;
  /**
   * Admin-managed storefront navigation, nested and ACTIVE-only.
   * An empty array is a valid answer — the header then falls back to its
   * built-in menus rather than rendering an empty bar.
   */
  getNav: (placement?: string) => Promise<StorefrontMenu[]>;
}

export interface PublicCatalogOptions {
  /**
   * Tenant code resolver. Returns the tenant code to send in the
   * `X-Tenant-Code` header for the next request. Resolved fresh per
   * call so the SPA can pivot to a different tenant (from hostname,
   * URL, dropdown, etc.) without recreating the service.
   *
   * Returning `undefined` skips the header — the backend will
   * fall back to its own resolution rules (Host header).
   */
  getTenantCode: () => string | undefined;
}

export function createPublicCatalogService(
  client: AxiosInstance,
  opts: PublicCatalogOptions,
): PublicCatalogService {
  const headers = (): Record<string, string> => {
    const code = opts.getTenantCode();
    return code ? { 'X-Tenant-Code': code } : {};
  };

  /**
   * POST one `/Public/products` query and hand back the RAW payload —
   * both callers need the paging envelope, which `unwrapItems` drops.
   *
   * Undefined fields are stripped rather than sent as `null`: the API
   * treats a present-but-null `MinPrice` the same as absent today, but
   * an explicit null is a filter the caller didn't ask for, and relying
   * on the server to ignore it is a coupling worth not having.
   */
  const postProducts = async (q: PublicProductQuery) => {
    const body: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(q)) {
      if (v !== undefined && v !== null && v !== '') body[k] = v;
    }
    const resp = await client.post('/Public/products', body, { headers: headers() });
    return resp.data;
  };

  /**
   * Pull the paging envelope off a raw `/Public/products` response.
   * Every field is optional: a plain-array response carries none of
   * them, and each caller decides its own fallback.
   */
  const pagingOf = (
    raw: unknown,
  ): { page?: number; pageSize?: number; total?: number; totalPages?: number } => {
    const d = (raw as { data?: Record<string, unknown> } | null)?.data;
    if (!d || Array.isArray(d)) return {};
    const num = (v: unknown): number | undefined => {
      const n = Number(v);
      return Number.isFinite(n) && n >= 0 ? n : undefined;
    };
    return {
      page: num(d.page),
      pageSize: num(d.pageSize),
      total: num(d.total),
      totalPages: num(d.totalPages),
    };
  };

  return {
    products: async (query = {}) => {
      const raw = await postProducts(query);
      const items = unwrapItems<Product>(raw);
      const env = pagingOf(raw);
      return {
        items,
        /* A server that answered with a plain array (no envelope) is
           reporting one page containing everything it sent — describing
           it as page 1 of 1 keeps pager maths honest rather than
           inventing a total the caller can't act on. */
        page: env.page ?? query.page ?? 1,
        pageSize: env.pageSize ?? query.pageSize ?? items.length,
        total: env.total ?? items.length,
        totalPages: env.totalPages ?? 1,
      };
    },

    allProducts: async () => {
      /* Server caps pageSize at 100 and defaults it to 24. Page 1 tells
         us `totalPages`, then the rest are fetched in parallel. */
      const PAGE_SIZE = 100;
      /* Safety valve: a runaway `totalPages` (bad tenant data, a server
         regression) shouldn't fan out into hundreds of requests. 50
         pages = 5,000 products, well past what any consumer of the full
         set can usefully hold. */
      const MAX_PAGES = 50;

      const first = await postProducts({ page: 1, pageSize: PAGE_SIZE });
      const items = unwrapItems<Product>(first);
      const totalPages = pagingOf(first).totalPages ?? 1;
      if (totalPages <= 1) return items;

      const last = Math.min(totalPages, MAX_PAGES);
      const rest = await Promise.all(
        Array.from({ length: last - 1 }, (_, i) =>
          postProducts({ page: i + 2, pageSize: PAGE_SIZE }),
        ),
      );
      for (const page of rest) items.push(...unwrapItems<Product>(page));
      return items;
    },
    productById: async (id) => {
      try {
        const resp = await client.post(
          `/Public/products/${encodeURIComponent(id)}`,
          {},
          { headers: headers() },
        );
        return unwrap<Product>(resp.data);
      } catch (e) {
        const status = (e as { response?: { status?: number } })?.response?.status;
        if (status === 404) return null;
        throw e;
      }
    },
    shipping: async () => {
      try {
        const resp = await client.post('/Public/shipping', {}, { headers: headers() });
        const d = unwrap<Partial<PublicShipping>>(resp.data);
        if (!d) return FREE_SHIPPING;
        const fee = Number(d.shippingCharge);
        const raw = d.freeShippingThreshold;
        const threshold = raw == null ? null : Number(raw);
        return {
          shippingCharge: Number.isFinite(fee) && fee > 0 ? fee : 0,
          freeShippingThreshold:
            threshold != null && Number.isFinite(threshold) && threshold >= 0 ? threshold : null,
        };
      } catch {
        /* Older API without /Public/shipping, or a transient failure.
           Falling back to free is deliberate — see FREE_SHIPPING. */
        return FREE_SHIPPING;
      }
    },
    categories: async () => {
      const resp = await client.post('/Public/categories', {}, { headers: headers() });
      return unwrapItems<Category>(resp.data);
    },
    banners: async () => {
      const resp = await client.post('/Public/banners', {}, { headers: headers() });
      return unwrapItems<Banner>(resp.data);
    },
    brands: async () => {
      const resp = await client.post('/Public/brands', {}, { headers: headers() });
      return unwrapItems<Brand>(resp.data);
    },
    getPage: async (slug: string) => {
      // Slug is a URL segment, so we use GET here (matches the backend
      // signature). 404 is the "no row yet" case — return null so the
      // caller can render a placeholder rather than treating it as an
      // error.
      try {
        const resp = await client.get(
          `/Public/pages/${encodeURIComponent(slug)}`,
          { headers: headers() },
        );
        return unwrap<PublicPage>(resp.data);
      } catch (e) {
        const status = (e as { response?: { status?: number } })?.response?.status;
        if (status === 404) return null;
        throw e;
      }
    },

    getNav: async (placement = 'header') => {
      const resp = await client.post(
        '/Public/nav',
        { placement },
        { headers: headers() },
      );
      return unwrapItems<StorefrontMenu>(resp.data);
    },
  };
}
